import { useCallback, useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

import { ModeToggle, type AppMode } from "../components/ModeToggle";
import "../components/ModeToggle.scss";

import { useLandingI18n } from "./i18n";

import "./LandingApp.scss";

import svgOpenclaw from "./assets/openclaw.svg";
import svgDog from "./assets/dog.svg";
import svgEconomic from "./assets/economic.svg";
import svgProductDevelopmentFlow from "./assets/product-development-flow.svg";
import svgTencentOrgStructure from "./assets/tencent-org-structure.svg";
import svgTransformer from "./assets/transformer.svg";

gsap.registerPlugin(ScrollTrigger);

const MOBILE_BREAKPOINT = 768;

const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth <= MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
};

// ─── Navbar ──────────────────────────────────────────────────────────────────

const Navbar = () => {
  const { t, lang, toggleLang } = useLandingI18n();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
      <div className="nav__inner">
        <a href="/" className="nav__logo">
          <span className="material-symbols-outlined nav__logo-icon">
            lightbulb
          </span>
          <span className="nav__logo-text">IdeaWeave</span>
        </a>
        <div className="nav__links">
          <button
            className="nav__lang"
            onClick={toggleLang}
            aria-label="Switch language"
          >
            {lang === "en" ? "中文" : "EN"}
          </button>
          <a href="#features" className="nav__link">
            {t("nav.features")}
          </a>
          <a href="/app" className="nav__cta">
            {t("nav.cta")}
          </a>
        </div>
      </div>
    </nav>
  );
};

// ─── Demo Video Modal ─────────────────────────────────────────────────────────

const YOUTUBE_VIDEO_ID = "keQl27MQJx8";

const DemoVideoModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overlayRef.current || !cardRef.current) {
      return;
    }

    if (open) {
      document.body.style.overflow = "hidden";
      gsap.to(overlayRef.current, {
        opacity: 1,
        duration: 0.35,
        ease: "power2.out",
        onStart: () => {
          overlayRef.current!.style.pointerEvents = "auto";
        },
      });
      gsap.fromTo(
        cardRef.current,
        { scale: 0.85, opacity: 0, y: 30 },
        { scale: 1, opacity: 1, y: 0, duration: 0.45, ease: "back.out(1.4)", delay: 0.1 },
      );
    } else {
      gsap.to(cardRef.current, {
        scale: 0.9,
        opacity: 0,
        y: 20,
        duration: 0.25,
        ease: "power2.in",
      });
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.3,
        delay: 0.1,
        ease: "power2.in",
        onComplete: () => {
          document.body.style.overflow = "";
          if (overlayRef.current) {
            overlayRef.current.style.pointerEvents = "none";
          }
        },
      });
    }
  }, [open]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <div
      ref={overlayRef}
      className="demo-modal"
      style={{ opacity: 0, pointerEvents: "none" }}
      onClick={(e) => {
        if (e.target === overlayRef.current) {
          onClose();
        }
      }}
    >
      <div ref={cardRef} className="demo-modal__card">
        <button
          className="demo-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
        {open && (
          <iframe
            className="demo-modal__iframe"
            src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
            title="IdeaWeave Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
    </div>
  );
};

// ─── Hero Section ────────────────────────────────────────────────────────────

const Hero = () => {
  const { t, heroWords, lang } = useLandingI18n();
  const ref = useRef<HTMLElement>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const closeDemoModal = useCallback(() => setDemoOpen(false), []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.from(".hero__badge", {
        y: 20,
        opacity: 0,
        duration: 0.6,
        delay: 0.2,
      })
        .from(
          ".hero__title .word",
          {
            y: 80,
            opacity: 0,
            rotateX: 40,
            duration: 0.9,
            stagger: 0.08,
          },
          "-=0.3",
        )
        .to(
          ".hero__highlight",
          {
            scaleX: 1,
            duration: 0.6,
            ease: "power2.out",
          },
          "-=0.1",
        )
        .add(() => {
          document.querySelectorAll(".hero__char").forEach((ch, i) => {
            gsap.to(ch, {
              y: -5,
              duration: 0.8,
              repeat: -1,
              yoyo: true,
              ease: "sine.inOut",
              delay: i * 0.12,
            });
          });
        })
        .from(
          ".hero__subtitle",
          {
            y: 30,
            opacity: 0,
            duration: 0.7,
          },
          "-=0.4",
        )
        .from(
          ".hero__actions",
          {
            y: 30,
            opacity: 0,
            duration: 0.7,
          },
          "-=0.4",
        )
        .from(
          ".hero__visual",
          {
            y: 60,
            opacity: 0,
            scale: 0.95,
            duration: 1,
          },
          "-=0.5",
        );

      gsap.to(".hero__shape--1", {
        y: -20,
        rotation: 10,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
      gsap.to(".hero__shape--2", {
        y: 15,
        rotation: -8,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        delay: 0.5,
      });
      gsap.to(".hero__shape--3", {
        y: -12,
        rotation: 5,
        duration: 2.8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        delay: 1,
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} className="hero">
      <div className="hero__bg">
        <div className="hero__gradient" />
        <div className="hero__grid" />
        <div className="hero__shape hero__shape--1" />
        <div className="hero__shape hero__shape--2" />
        <div className="hero__shape hero__shape--3" />
      </div>

      <div className="hero__content">
        <div className="hero__badge">
          <span className="hero__badge-dot" />
          {t("hero.badge")}
        </div>

        <h1 className="hero__title" data-lang={lang}>
          {heroWords.map((w, i) =>
            i === heroWords.length - 1 ? (
              <span key={i} className="word word--visually">
                {w.split("").map((ch, j) => (
                  <span key={j} className="hero__char">
                    {ch}
                  </span>
                ))}
                <em className="hero__highlight" />
              </span>
            ) : (
              <span key={i} className="word">
                {w}
              </span>
            ),
          )}
        </h1>

        <p className="hero__subtitle">{t("hero.subtitle")}</p>

        <div className="hero__actions">
          <a href="/app" className="btn btn--primary">
            <span>{t("hero.primaryCta")}</span>
            <span className="material-symbols-outlined">arrow_forward</span>
          </a>
          <a href="#how-it-works" className="btn btn--ghost">
            {t("hero.ghostCta")}
          </a>
        </div>
      </div>

      <div className="hero__visual">
        <ProductDemo />
      </div>

      <button
        className="hero__demo-btn"
        onClick={() => setDemoOpen(true)}
        type="button"
      >
        <span className="hero__demo-tv">
          <span className="hero__demo-screen">
            <span className="hero__demo-play">
              <span className="material-symbols-outlined">play_arrow</span>
            </span>
            <span className="hero__demo-scanline" />
          </span>
          <span className="hero__demo-antenna hero__demo-antenna--l" />
          <span className="hero__demo-antenna hero__demo-antenna--r" />
          <span className="hero__demo-stand" />
        </span>
        <span className="hero__demo-label">
          <span className="hero__demo-title">{t("hero.watchDemo")}</span>
          <span className="hero__demo-duration">{t("hero.demoDuration")}</span>
        </span>
      </button>

      <DemoVideoModal open={demoOpen} onClose={closeDemoModal} />

      <div className="hero__scroll-hint">
        <div className="hero__scroll-mouse" />
        <span className="hero__scroll-text">{t("hero.scroll")}</span>
      </div>
    </section>
  );
};

// ─── Product Demo (SVG carousel) ─────────────────────────────────────────────

const DEMO_SLIDES = [
  svgProductDevelopmentFlow,
  svgOpenclaw,
  svgTencentOrgStructure,
  svgTransformer,
  svgEconomic,
  svgDog,
];
const SLIDE_DURATION = 3500;
const FADE_DURATION = 600;

const ProductDemo = () => {
  const { t } = useLandingI18n();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % DEMO_SLIDES.length);
    }, SLIDE_DURATION);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="demo">
      <div className="demo__browser">
        <div className="demo__browser-bar">
          <span className="demo__dot demo__dot--r" />
          <span className="demo__dot demo__dot--y" />
          <span className="demo__dot demo__dot--g" />
          <span className="demo__browser-url">ideaweave.com</span>
          <span className="demo__example-badge">{t("hero.demoLabel")}</span>
        </div>
        <div className="demo__screen demo__screen--carousel">
          {DEMO_SLIDES.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Demo diagram ${i + 1}`}
              className={`demo__slide${
                i === active ? " demo__slide--active" : ""
              }`}
              style={{ transitionDuration: `${FADE_DURATION}ms` }}
              draggable={false}
            />
          ))}
          <div className="demo__indicators">
            {DEMO_SLIDES.map((_, i) => (
              <span
                key={i}
                className={`demo__dot-indicator${
                  i === active ? " demo__dot-indicator--active" : ""
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Step Sections ───────────────────────────────────────────────────────────

interface StepProps {
  index: number;
  id: string;
  label: string;
  title: string;
  description: string;
  children: React.ReactNode;
  reversed?: boolean;
  pinned?: boolean;
}

const Step = ({
  index,
  id,
  label,
  title,
  description,
  children,
  reversed,
  pinned,
}: StepProps) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (pinned) {
      return;
    }
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: "top 75%",
          ...(isMobile ? {} : { end: "bottom 25%" }),
          toggleActions: isMobile
            ? "play none none none"
            : "play none none reverse",
        },
      });

      tl.from(".step__number", {
        scale: 0,
        opacity: 0,
        duration: 0.5,
        ease: "back.out(2)",
      })
        .from(".step__label", { x: -20, opacity: 0, duration: 0.4 }, "-=0.2")
        .from(".step__title", { y: 30, opacity: 0, duration: 0.6 }, "-=0.2")
        .from(".step__desc", { y: 20, opacity: 0, duration: 0.5 }, "-=0.3")
        .from(
          ".step__visual",
          {
            x: reversed ? -60 : 60,
            opacity: 0,
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.5",
        );
    }, ref);
    return () => ctx.revert();
  }, [reversed, pinned]);

  return (
    <section
      ref={ref}
      id={id}
      className={`step ${reversed ? "step--reversed" : ""} ${
        pinned ? "step--pinned" : ""
      }`}
    >
      <div className="step__content">
        <div className="step__meta">
          <div className="step__number">{String(index).padStart(2, "0")}</div>
          <div className="step__label">{label}</div>
        </div>
        <h2 className="step__title">{title}</h2>
        <p className="step__desc">{description}</p>
      </div>
      <div className="step__visual">{children}</div>
    </section>
  );
};

// ─── Voice Input Visual ──────────────────────────────────────────────────────

const VoiceVisual = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(".voice__ring", {
        scale: 2,
        opacity: 0,
        duration: 1.5,
        stagger: 0.4,
        repeat: -1,
        ease: "power1.out",
      });

      gsap.to(".voice__bar", {
        scaleY: "random(0.3, 1)",
        duration: 0.3,
        stagger: {
          each: 0.05,
          repeat: -1,
          yoyo: true,
        },
        ease: "sine.inOut",
      });

      const text = ref.current?.querySelector(".voice__typed");
      if (text) {
        const full =
          "I want to explain how OpenAI's Lobster Bot went viral — it uses tool-calling to auto-bid on lobsters in real time...";
        let i = 0;
        const type = () => {
          if (i <= full.length) {
            text.textContent = full.slice(0, i) + (i < full.length ? "|" : "");
            i++;
            setTimeout(type, 40 + Math.random() * 30);
          } else {
            setTimeout(() => {
              i = 0;
              type();
            }, 3000);
          }
        };
        setTimeout(type, 1000);
      }
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={ref} className="voice">
      <div className="voice__mockup">
        <div className="voice__input-bar">
          <div className="voice__mic-area">
            <div className="voice__mic-btn">
              <span className="material-symbols-outlined">mic</span>
            </div>
            <div className="voice__ring" />
            <div className="voice__ring" />
            <div className="voice__ring" />
          </div>
          <div className="voice__waveform">
            {Array.from({ length: 32 }).map((_, i) => (
              <div key={i} className="voice__bar" />
            ))}
          </div>
        </div>
        <div className="voice__output">
          <span className="voice__typed" />
        </div>
      </div>
    </div>
  );
};

// ─── Chat & Brainstorm Visual ────────────────────────────────────────────────

const ChatVisual = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const mobile = window.innerWidth <= MOBILE_BREAKPOINT;

    const msgs = el.querySelectorAll(".chat__msg");
    const bar = el.querySelector(".chat__bar");
    const goBtn = el.querySelector(".chat__bar-btn--go");
    const dots = el.querySelectorAll(".chat__typing-dots span");

    gsap.set([msgs, bar], { opacity: 0, y: 20 });

    let tl: gsap.core.Timeline;

    if (mobile) {
      tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: "top 60%",
          toggleActions: "play none none none",
        },
      });

      tl.to(msgs[0], { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" })
        .to(msgs[1], { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }, "+=0.8")
        .to(msgs[2], { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }, "+=0.8")
        .from(dots, {
          scale: 0,
          opacity: 0,
          duration: 0.2,
          stagger: 0.08,
          ease: "back.out(2)",
        })
        .to(bar, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, "+=1")
        .fromTo(
          goBtn,
          { scale: 0.8, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.35, ease: "back.out(2)" },
          "+=0.3",
        );
    } else {
      const parentStep = el.closest(".step");
      if (!parentStep) {
        return;
      }

      const barOrganize = el.querySelector(".chat__bar-btn--organize");
      const barGenerate = el.querySelector(".chat__bar-btn--highlight");

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: parentStep,
          start: "top top",
          end: "+=180%",
          pin: true,
          scrub: 0.8,
          anticipatePin: 1.5,
          pinSpacing: "margin",
        },
      });

      tl.to(msgs[0], { opacity: 1, y: 0, duration: 0.3 })
        .to(msgs[1], { opacity: 1, y: 0, duration: 0.3 }, "+=0.1")
        .to(bar, { opacity: 1, y: 0, duration: 0.2 }, "+=0.05")
        .fromTo(
          barOrganize,
          { scale: 0.85, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.2, ease: "back.out(1.5)" },
          "+=0.05",
        )
        .to(msgs[2], { opacity: 1, y: 0, duration: 0.3 }, "+=0.05")
        .from(dots, {
          scale: 0,
          opacity: 0,
          duration: 0.15,
          stagger: 0.05,
          ease: "back.out(2)",
        })
        .fromTo(
          barGenerate,
          { scale: 0.85, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 0.2,
            ease: "back.out(1.5)",
          },
          "+=0.05",
        )
        .fromTo(
          goBtn,
          { scale: 0.8, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.25, ease: "back.out(2)" },
          "+=0.1",
        );
    }

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <div ref={ref} className="chat">
      <div className="chat__window">
        <div className="chat__messages">
          <div className="chat__msg chat__msg--user">
            <div className="chat__avatar chat__avatar--user">You</div>
            <div className="chat__text">
              I want to explain how OpenAI&apos;s Lobster Bot works — the viral
              one that auto-bids on lobsters
            </div>
          </div>
          <div className="chat__msg chat__msg--ai">
            <div className="chat__avatar chat__avatar--ai">
              <span className="material-symbols-outlined">lightbulb</span>
            </div>
            <div className="chat__text">
              {isMobile
                ? "Great topic! We can unpack tool-calling, the bidding loop, and why it went viral—ask follow-ups anytime."
                : "Great topic! This is about AI agents using tool-calling APIs. Let me organize the key concepts: how tool-calling works, the bidding loop, and why it went viral."}
            </div>
          </div>
          <div className="chat__msg chat__msg--ai">
            <div className="chat__avatar chat__avatar--ai">
              <span className="material-symbols-outlined">lightbulb</span>
            </div>
            <div className="chat__text">
              {isMobile
                ? "Want to go deeper or tweak the angle? When you're ready, continue on the canvas."
                : "I've structured your content into 4 sections. Ready to generate a visual diagram plan?"}
              <div className="chat__typing-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>

        <div className="chat__bar">
          {!isMobile && (
            <div className="chat__bar-left">
              <div className="chat__bar-btn chat__bar-btn--active">
                <span className="material-symbols-outlined">chat</span>
              </div>
              <div className="chat__bar-btn chat__bar-btn--organize">
                <span className="material-symbols-outlined">folder_open</span>
                <span className="chat__bar-label">Organize Content</span>
              </div>
              <div className="chat__bar-btn chat__bar-btn--highlight">
                <span className="material-symbols-outlined">account_tree</span>
                <span className="chat__bar-label">Generate Diagram Plan</span>
              </div>
            </div>
          )}
          <button type="button" className="chat__bar-btn chat__bar-btn--go">
            Go to Draw
            <span className="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Canvas Visual ───────────────────────────────────────────────────────────

const CanvasVisual = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const paths = ref.current?.querySelectorAll(".canvas__draw-path");
      paths?.forEach((path) => {
        const el = path as SVGPathElement;
        const length = el.getTotalLength();
        gsap.set(el, { strokeDasharray: length, strokeDashoffset: length });
        gsap.to(el, {
          strokeDashoffset: 0,
          duration: 1.5,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: ref.current,
            start: "top 60%",
            toggleActions: "play none none none",
          },
          onComplete: () => {
            gsap.set(el, { strokeDasharray: "8 12", strokeDashoffset: 0 });
            gsap.to(el, {
              strokeDashoffset: -40,
              duration: 1.5,
              ease: "none",
              repeat: -1,
            });
          },
        });
      });

      gsap.from(".canvas__box", {
        scale: 0,
        opacity: 0,
        duration: 0.5,
        stagger: 0.15,
        ease: "back.out(2)",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 60%",
          toggleActions: "play none none none",
        },
      });

      gsap.from(".canvas__sidebar", {
        x: 80,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 50%",
          toggleActions: "play none none none",
        },
        delay: 0.8,
      });

      gsap.from(".canvas__badge", {
        scale: 0,
        duration: 0.5,
        ease: "back.out(3)",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 50%",
          toggleActions: "play none none none",
        },
        delay: 1.3,
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={ref} className="canvas">
      <div className="canvas__frame">
        <div className="canvas__toolbar">
          <span className="canvas__tool">
            <span className="material-symbols-outlined">pan_tool</span>
          </span>
          <span className="canvas__tool canvas__tool--active">
            <span className="material-symbols-outlined">crop_square</span>
          </span>
          <span className="canvas__tool">
            <span className="material-symbols-outlined">horizontal_rule</span>
          </span>
          <span className="canvas__tool">
            <span className="material-symbols-outlined">text_fields</span>
          </span>
        </div>

        <div className="canvas__area">
          <svg className="canvas__svg" viewBox="0 0 500 280">
            {/* Paths */}
            <path
              className="canvas__draw-path"
              d="M145,55 C180,55 180,100 215,100"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
            />
            <path
              className="canvas__draw-path"
              d="M145,55 C180,55 180,200 215,200"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
            />
            <path
              className="canvas__draw-path"
              d="M355,100 C390,100 390,150 425,150"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
            />
            <path
              className="canvas__draw-path"
              d="M355,200 C390,200 390,150 425,150"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
            />

            {/* Boxes */}
            <g className="canvas__box">
              <rect
                x="15"
                y="30"
                width="130"
                height="50"
                rx="10"
                fill="#fef7ec"
                stroke="#d48a2e"
                strokeWidth="2"
              />
              <text
                x="80"
                y="60"
                textAnchor="middle"
                fill="#c07822"
                fontSize="13"
                fontWeight="600"
              >
                User Prompt
              </text>
            </g>
            <g className="canvas__box">
              <rect
                x="215"
                y="75"
                width="140"
                height="50"
                rx="10"
                fill="#eef4ff"
                stroke="#4f8fe8"
                strokeWidth="2"
              />
              <text
                x="285"
                y="105"
                textAnchor="middle"
                fill="#2563eb"
                fontSize="13"
                fontWeight="600"
              >
                AI Agent
              </text>
            </g>
            <g className="canvas__box">
              <rect
                x="215"
                y="175"
                width="140"
                height="50"
                rx="10"
                fill="#ecfdf5"
                stroke="#059669"
                strokeWidth="2"
              />
              <text
                x="285"
                y="205"
                textAnchor="middle"
                fill="#065f46"
                fontSize="13"
                fontWeight="600"
              >
                Tool Calling
              </text>
            </g>
            <g className="canvas__box">
              <rect
                x="425"
                y="125"
                width="60"
                height="50"
                rx="10"
                fill="#faf3e4"
                stroke="#d97706"
                strokeWidth="2"
              />
              <text
                x="455"
                y="155"
                textAnchor="middle"
                fill="#b45309"
                fontSize="11"
                fontWeight="600"
              >
                Result
              </text>
            </g>
          </svg>
        </div>

        <div className="canvas__sidebar">
          <div className="canvas__sidebar-title">
            <span className="material-symbols-outlined">edit</span>
            AI Weaver
          </div>
          <div className="canvas__sidebar-msg">
            &ldquo;Add a feedback loop between Tool Calling and AI Agent&rdquo;
          </div>
          <div className="canvas__sidebar-actions">
            <span className="canvas__sidebar-btn">Apply</span>
            <span className="canvas__sidebar-btn canvas__sidebar-btn--ghost">
              Edit manually
            </span>
          </div>
        </div>

        <div className="canvas__badge">
          <span className="material-symbols-outlined">touch_app</span>
          Fully Editable
        </div>
      </div>
    </div>
  );
};

// ─── Recording Visual ────────────────────────────────────────────────────────

const RecordingVisual = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(".rec__bar", {
        scaleY: "random(0.2, 1)",
        duration: 0.4,
        stagger: {
          each: 0.06,
          repeat: -1,
          yoyo: true,
        },
        ease: "sine.inOut",
      });

      const words = ref.current?.querySelectorAll(".rec__word");
      if (words) {
        gsap.from(words, {
          opacity: 0,
          y: 10,
          duration: 0.3,
          stagger: 0.15,
          scrollTrigger: {
            trigger: ref.current,
            start: "top 60%",
            toggleActions: "play none none none",
          },
          delay: 0.5,
        });
      }

      gsap.from(".rec__cam-preview", {
        scale: 0,
        opacity: 0,
        duration: 0.6,
        ease: "back.out(2)",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 50%",
          toggleActions: "play none none none",
        },
        delay: 1,
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  const scriptWords =
    "So first, what makes this bot special? It uses Claude's tool-calling feature. The AI doesn't just chat — it can call real APIs. In this case, it monitors lobster prices and auto-bids when conditions are right.".split(
      " ",
    );

  return (
    <div ref={ref} className="rec">
      <div className="rec__player">
        <div className="rec__header">
          <div className="rec__status">
            <span className="rec__live-dot" />
            Recording Mode
          </div>
          <div className="rec__time">01:42</div>
        </div>

        <div className="rec__waveform">
          {Array.from({ length: 40 }).map((_, i) => (
            <div
              key={i}
              className="rec__bar"
              style={
                {
                  "--h": `${30 + Math.sin(i * 0.4) * 40 + Math.random() * 20}%`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className="rec__script">
          <div className="rec__script-label">
            <span className="material-symbols-outlined">auto_awesome</span>
            AI Teleprompter
          </div>
          <p className="rec__script-text">
            {scriptWords.map((word, i) => (
              <span key={i} className="rec__word">
                {word}{" "}
              </span>
            ))}
          </p>
        </div>

        <div className="rec__controls">
          <span className="rec__ctrl">
            <span className="material-symbols-outlined">skip_previous</span>
          </span>
          <span className="rec__ctrl rec__ctrl--play">
            <span className="material-symbols-outlined">pause</span>
          </span>
          <span className="rec__ctrl">
            <span className="material-symbols-outlined">skip_next</span>
          </span>
        </div>

        <div className="rec__cam-preview">
          <span className="material-symbols-outlined">person</span>
        </div>
      </div>
    </div>
  );
};

// ─── Workflow Timeline ───────────────────────────────────────────────────────

const WorkflowTimeline = () => {
  const { t } = useLandingI18n();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const titleEl = el.querySelector(".timeline__title");
    const stepEls = el.querySelectorAll(".timeline__step");
    const connectorEls = el.querySelectorAll(".timeline__connector");
    const labelEls = el.querySelectorAll(".timeline__label");

    gsap.set([titleEl, stepEls, connectorEls, labelEls], {
      opacity: 0,
    });
    gsap.set(titleEl, { y: 40, scale: 0.9 });
    gsap.set(stepEls, { y: 30, scale: 0.6 });
    gsap.set(connectorEls, { scaleX: 0 });
    gsap.set(labelEls, { y: 15 });

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    const tl = gsap.timeline({
      scrollTrigger: isMobile
        ? {
            trigger: el,
            start: "top 60%",
            toggleActions: "play none none none",
          }
        : {
            trigger: el,
            start: "top 10%",
            end: "+=130%",
            pin: true,
            scrub: 0.6,
            anticipatePin: 1.5,
            pinSpacing: "margin",
          },
    });

    tl.to(titleEl, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: isMobile ? 0.35 : 0.4,
      ease: "power3.out",
    });

    stepEls.forEach((step, i) => {
      tl.to(
        step,
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.25,
          ease: "back.out(1.8)",
        },
        i === 0 ? "+=0.1" : isMobile ? "+=0.05" : "-=0.1",
      );
      if (connectorEls[i - 1]) {
        tl.to(
          connectorEls[i - 1],
          { opacity: 1, scaleX: 1, duration: 0.2, ease: "power2.inOut" },
          "<0.05",
        );
      }
      tl.to(
        labelEls[i],
        { opacity: 1, y: 0, duration: 0.15, ease: "power2.out" },
        isMobile ? "<0.05" : "<0.08",
      );
    });

    return () => {
      tl.kill();
    };
  }, []);

  const steps = [
    { icon: "mic", label: t("timeline.speak"), color: "#d48a2e" },
    { icon: "psychology", label: t("timeline.brainstorm"), color: "#4f8fe8" },
    { icon: "draw", label: t("timeline.draw"), color: "#059669" },
    { icon: "slideshow", label: t("timeline.present"), color: "#db2777" },
  ];

  return (
    <section ref={ref} className="timeline">
      <h2 className="timeline__title">{t("timeline.title")}</h2>
      <div className="timeline__track">
        {steps.map((s, i) => (
          <div key={i} className="timeline__item">
            {i > 0 && <div className="timeline__connector" />}
            <div
              className="timeline__step"
              style={{ "--clr": s.color } as React.CSSProperties}
            >
              <span className="material-symbols-outlined">{s.icon}</span>
            </div>
            <span className="timeline__label">{s.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── CTA Footer ──────────────────────────────────────────────────────────────

const CTAFooter = () => {
  const { t } = useLandingI18n();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const ctx = gsap.context(() => {
      gsap.from(".cta__title", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: isMobile ? "top 60%" : "top 75%",
          toggleActions: isMobile
            ? "play none none none"
            : "play none none reverse",
        },
      });
      gsap.from(".cta__btn", {
        y: 20,
        opacity: 0,
        duration: 0.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: isMobile ? "top 55%" : "top 70%",
          toggleActions: isMobile
            ? "play none none none"
            : "play none none reverse",
        },
        delay: 0.3,
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} className="cta">
      <div className="cta__glow" />
      <h2 className="cta__title">{t("cta.title")}</h2>
      <a href="/app" className="btn btn--primary btn--lg cta__btn">
        <span>{t("cta.btn")}</span>
        <span className="material-symbols-outlined">arrow_forward</span>
      </a>
      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <span className="material-symbols-outlined">lightbulb</span>
            IdeaWeave
          </div>
          <div className="footer__links">
            <a href="/privacy">{t("footer.privacy")}</a>
            <a href="/terms">{t("footer.terms")}</a>
          </div>
        </div>
      </footer>
    </section>
  );
};

// ─── Draw Showcase ────────────────────────────────────────────────────────────

const DrawShowcase = () => {
  const { t } = useLandingI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const [drawMode] = useState<AppMode>("draw");

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) {
      return;
    }

    const toggleEl = el.querySelector(".draw-showcase__toggle");
    const stepContent = el.querySelector(".draw-showcase__step-content");
    const visual = el.querySelector(".draw-showcase__visual");
    const inputBox = el.querySelector(".draw-showcase__input");
    const boltFlash = el.querySelector(".draw-showcase__bolt-flash");
    const svgPaths = el.querySelectorAll(".draw-showcase__diagram-path");
    const svgBoxes = el.querySelectorAll(".draw-showcase__diagram-box");
    const badge = el.querySelector(".draw-showcase__done-badge");

    gsap.set([toggleEl, stepContent, visual], { opacity: 0, y: 40 });
    gsap.set(inputBox, { opacity: 0, scaleX: 0.8 });
    gsap.set(boltFlash, { opacity: 0, scale: 0 });
    gsap.set(svgBoxes, { opacity: 0, scale: 0 });
    gsap.set(badge, { opacity: 0, scale: 0 });
    svgPaths.forEach((path) => {
      const p = path as SVGPathElement;
      const len = p.getTotalLength();
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
    });

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    gsap.to([toggleEl, stepContent, visual], {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.15,
      ease: "power3.out",
      scrollTrigger: {
        trigger: el,
        start: "top 85%",
        toggleActions: isMobile
          ? "play none none none"
          : "play none none reverse",
      },
    });

    const onPathsComplete = () => {
      svgPaths.forEach((path) => {
        const p = path as SVGPathElement;
        gsap.set(p, { strokeDasharray: "8 12", strokeDashoffset: 0 });
        gsap.to(p, {
          strokeDashoffset: -40,
          duration: 1.5,
          ease: "none",
          repeat: -1,
        });
      });
    };

    let tl: gsap.core.Timeline;

    if (isMobile) {
      tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 50%",
          toggleActions: "play none none none",
        },
      });

      tl.to(inputBox, { opacity: 1, scaleX: 1, duration: 0.5, ease: "power2.out" })
        .to(boltFlash, { opacity: 1, scale: 1.3, duration: 0.3, ease: "back.out(2)" }, "+=0.3")
        .to(boltFlash, { opacity: 0.6, scale: 1, duration: 0.15 })
        .addLabel("drawDiagram")
        .to(svgBoxes, { opacity: 1, scale: 1, duration: 0.4, stagger: 0.1, ease: "back.out(2)" }, "drawDiagram")
        .to(svgPaths, { strokeDashoffset: 0, duration: 0.6, stagger: 0.08, ease: "power2.inOut", onComplete: onPathsComplete }, "drawDiagram+=0.15")
        .to(badge, { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(3)" }, "+=0.3");
    } else {
      tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 8%",
          end: "+=80%",
          pin: true,
          scrub: 0.5,
          anticipatePin: 1.5,
          pinSpacing: "margin",
        },
      });

      tl.to(inputBox, { opacity: 1, scaleX: 1, duration: 0.15, ease: "power2.out" })
        .to(boltFlash, { opacity: 1, scale: 1.3, duration: 0.1, ease: "back.out(2)" }, "+=0.02")
        .to(boltFlash, { opacity: 0.6, scale: 1, duration: 0.05 })
        .addLabel("drawDiagram")
        .to(svgBoxes, { opacity: 1, scale: 1, duration: 0.15, stagger: 0.02, ease: "back.out(2)" }, "drawDiagram")
        .to(svgPaths, { strokeDashoffset: 0, duration: 0.15, stagger: 0.02, ease: "power2.inOut", onComplete: onPathsComplete }, "drawDiagram+=0.04")
        .to(badge, { opacity: 1, scale: 1, duration: 0.08, ease: "back.out(3)" }, "+=0.01");
    }

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <section ref={sectionRef} className="draw-showcase">
      <div className="draw-showcase__inner">
        <div className="draw-showcase__toggle">
          <ModeToggle
            mode={drawMode}
            onChange={() => {}}
            drawLabel={t("draw.showcase.drawLabel")}
            drawDesc={t("draw.showcase.drawDesc")}
            weaveLabel={t("draw.showcase.weaveLabel")}
            weaveDesc={t("draw.showcase.weaveDesc")}
            size="large"
          />
        </div>

        <div className="draw-showcase__body">
          <div className="draw-showcase__step-content">
            <span className="draw-showcase__tag">{t("draw.showcase.tag")}</span>
            <h2 className="draw-showcase__heading">
              {t("draw.showcase.heading")}
            </h2>
            <p className="draw-showcase__desc">{t("draw.showcase.desc")}</p>
          </div>

          <div className="draw-showcase__visual">
            <div className="draw-showcase__mockup">
              <div className="draw-showcase__input">
                <span className="material-symbols-outlined draw-showcase__input-icon">
                  edit
                </span>
                <span className="draw-showcase__input-text">
                  {t("draw.showcase.inputPlaceholder")}
                </span>
              </div>
              <div className="draw-showcase__bolt-flash">
                <span className="material-symbols-outlined">bolt</span>
              </div>
              <div className="draw-showcase__diagram-area">
                <svg
                  className="draw-showcase__diagram-svg"
                  viewBox="0 0 300 160"
                >
                  {/* Row 1 */}
                  <rect
                    className="draw-showcase__diagram-box"
                    x="10"
                    y="10"
                    width="120"
                    height="44"
                    rx="10"
                    fill="#fef7ee"
                    stroke="#d48a2e"
                    strokeWidth="2"
                  />
                  <text
                    x="70"
                    y="37"
                    textAnchor="middle"
                    fill="#92400e"
                    fontSize="12"
                    fontWeight="700"
                  >
                    💡 Your Idea
                  </text>
                  <rect
                    className="draw-showcase__diagram-box"
                    x="170"
                    y="10"
                    width="120"
                    height="44"
                    rx="10"
                    fill="#eef4ff"
                    stroke="#4f8fe8"
                    strokeWidth="2"
                  />
                  <text
                    x="230"
                    y="37"
                    textAnchor="middle"
                    fill="#1e40af"
                    fontSize="12"
                    fontWeight="700"
                  >
                    🔍 AI Search
                  </text>
                  {/* Row 2 */}
                  <rect
                    className="draw-showcase__diagram-box"
                    x="10"
                    y="106"
                    width="120"
                    height="44"
                    rx="10"
                    fill="#f3f0ff"
                    stroke="#8b5cf6"
                    strokeWidth="2"
                  />
                  <text
                    x="70"
                    y="133"
                    textAnchor="middle"
                    fill="#5b21b6"
                    fontSize="12"
                    fontWeight="700"
                  >
                    📋 Auto Plan
                  </text>
                  <rect
                    className="draw-showcase__diagram-box"
                    x="170"
                    y="106"
                    width="120"
                    height="44"
                    rx="10"
                    fill="#ecfdf5"
                    stroke="#059669"
                    strokeWidth="2"
                  />
                  <text
                    x="230"
                    y="133"
                    textAnchor="middle"
                    fill="#065f46"
                    fontSize="12"
                    fontWeight="700"
                  >
                    ✨ Diagram
                  </text>
                  {/* Arrows: Idea → Search (right) */}
                  <path
                    className="draw-showcase__diagram-path"
                    d="M130,32 L170,32"
                    fill="none"
                    stroke="#94A3B8"
                    strokeWidth="2"
                  />
                  <polygon
                    className="draw-showcase__diagram-box"
                    points="167,27 173,32 167,37"
                    fill="#94A3B8"
                  />
                  {/* Arrows: Search → Plan (down-left) */}
                  <path
                    className="draw-showcase__diagram-path"
                    d="M230,54 L230,76 L70,76 L70,106"
                    fill="none"
                    stroke="#94A3B8"
                    strokeWidth="2"
                  />
                  <polygon
                    className="draw-showcase__diagram-box"
                    points="65,103 70,109 75,103"
                    fill="#94A3B8"
                  />
                  {/* Arrows: Plan → Diagram (right) */}
                  <path
                    className="draw-showcase__diagram-path"
                    d="M130,128 L170,128"
                    fill="none"
                    stroke="#94A3B8"
                    strokeWidth="2"
                  />
                  <polygon
                    className="draw-showcase__diagram-box"
                    points="167,123 173,128 167,133"
                    fill="#94A3B8"
                  />
                </svg>
              </div>
              <div className="draw-showcase__done-badge">
                <span className="material-symbols-outlined">check_circle</span>
                Done
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Mode Switcher (Pin Transition) ──────────────────────────────────────────

const ModeSwitcher = ({
  onModeChange,
}: {
  onModeChange?: (mode: AppMode) => void;
}) => {
  const { t } = useLandingI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const [switcherMode, setSwitcherMode] = useState<AppMode>("draw");

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) {
      return;
    }

    const drawGroup = el.querySelector(".mode-switcher__draw-group");
    const weaveGroup = el.querySelector(".mode-switcher__weave-group");
    const toggleWrap = el.querySelector(".mode-switcher__toggle-wrap");

    gsap.set([drawGroup, toggleWrap], { opacity: 0, y: 30 });
    gsap.set(weaveGroup, { opacity: 0, y: 20 });

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    let tl: gsap.core.Timeline;

    if (isMobile) {
      const setMode = (mode: AppMode) => {
        setSwitcherMode(mode);
        onModeChange?.(mode);
      };

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 60%",
          toggleActions: "play none none reverse",
          onLeaveBack: () => setMode("draw"),
        },
      });

      tl.call(() => setMode("draw"))
        .to(drawGroup, { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" })
        .to(
          toggleWrap,
          { opacity: 1, y: 0, duration: 0.35, ease: "back.out(1.5)" },
          "-=0.15",
        )
        .to({}, { duration: 1.20 })
        .addLabel("switch")
        .to(
          drawGroup,
          { opacity: 0, y: -15, duration: 0.25, ease: "power2.inOut" },
          "switch",
        )
        .call(
          () => {
            if (!tl.reversed()) {
              setMode("weave");
            }
          },
          undefined,
          "switch+=0.05",
        )
        .to(
          weaveGroup,
          { opacity: 1, y: 0, duration: 0.35, ease: "power3.out" },
          "switch+=0.12",
        )
        .to({}, { duration: 0.2 });
    } else {
      const switchLabel = { progress: 0 };

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "+=120%",
          pin: true,
          scrub: 0.5,
          anticipatePin: 1.5,
          pinSpacing: "margin",
          onUpdate: (self) => {
            const threshold = switchLabel.progress;
            const newMode: AppMode =
              self.progress > threshold ? "weave" : "draw";
            setSwitcherMode(newMode);
            onModeChange?.(newMode);
          },
        },
      });

      tl.to(drawGroup, {
        opacity: 1,
        y: 0,
        duration: 0.15,
        ease: "power3.out",
      })
        .to(
          toggleWrap,
          { opacity: 1, y: 0, duration: 0.12, ease: "back.out(1.5)" },
          "-=0.06",
        )
        .to({}, { duration: 0.1 })
        .addLabel("switch")
        .to(
          drawGroup,
          { opacity: 0, y: -15, duration: 0.08, ease: "power2.in" },
          "switch",
        )
        .to(
          weaveGroup,
          { opacity: 1, y: 0, duration: 0.12, ease: "power3.out" },
          "switch+=0.08",
        )
        .to({}, { duration: 0.1 });

      switchLabel.progress = tl.labels.switch / tl.duration();
    }

    return () => {
      tl.kill();
    };
  }, [onModeChange]);

  return (
    <section ref={sectionRef} className="mode-switcher">
      <div className="mode-switcher__inner">
        {/* Text area — draw and weave text stacked in same space */}
        <div className="mode-switcher__text-area">
          <div className="mode-switcher__draw-group">
            <h2 className="mode-switcher__draw-title">{t("switcher.title")}</h2>
            <p className="mode-switcher__draw-subtitle">
              {t("switcher.subtitle")}
            </p>
          </div>
          <div className="mode-switcher__weave-group">
            <span className="mode-switcher__weave-tag">
              {t("weave.showcase.tag")}
            </span>
            <h2 className="mode-switcher__weave-title">
              {t("weave.showcase.heading")}
            </h2>
            <p className="mode-switcher__weave-subtitle">
              {t("weave.showcase.desc")}
            </p>
          </div>
        </div>

        {/* Toggle always visible below text */}
        <div className="mode-switcher__toggle-wrap">
          <ModeToggle
            mode={switcherMode}
            onChange={() => {}}
            drawLabel={t("draw.showcase.drawLabel")}
            drawDesc={t("draw.showcase.drawDesc")}
            weaveLabel={t("draw.showcase.weaveLabel")}
            weaveDesc={t("draw.showcase.weaveDesc")}
            size="large"
          />
        </div>

        {/* Down arrow appears after weave transition */}
        <div className="mode-switcher__arrow">
          <span className="material-symbols-outlined">arrow_downward</span>
        </div>
      </div>
    </section>
  );
};

// ─── Main App ────────────────────────────────────────────────────────────────

const LandingApp = () => {
  const { t, lang } = useLandingI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [weaveActive, setWeaveActive] = useState(false);
  const isMobile = useIsMobile();

  const handleModeChange = useCallback((m: AppMode) => {
    setWeaveActive(m === "weave");
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    lenis.on("scroll", ScrollTrigger.update);

    const raf = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    // Recalculate pin positions after web fonts finish loading —
    // CJK fonts are large and shift section heights on arrival.
    document.fonts.ready.then(() => {
      requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    // Recalculate after all resources (images, SVGs) finish loading —
    // late-loading assets shift section heights and invalidate pin math.
    const handleLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", handleLoad);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => ScrollTrigger.refresh(), 250);
    };
    window.addEventListener("resize", handleResize, { passive: true });

    // Safety-net refresh for any late layout shifts (lazy fonts, async CSS).
    const safetyRefresh = setTimeout(() => ScrollTrigger.refresh(), 2000);

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      window.removeEventListener("load", handleLoad);
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
      clearTimeout(safetyRefresh);
    };
  }, []);

  // Refresh ScrollTrigger whenever language changes — section heights differ between EN / ZH.
  useEffect(() => {
    const timer = setTimeout(() => ScrollTrigger.refresh(), 150);
    return () => clearTimeout(timer);
  }, [lang]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("a[href^='#']");
      if (target) {
        e.preventDefault();
        const id = target.getAttribute("href")!.slice(1);
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`landing${weaveActive ? " landing--weave-active" : ""}`}
    >
      <Navbar />
      <Hero />

      <DrawShowcase />
      <ModeSwitcher onModeChange={handleModeChange} />

      <div id="how-it-works" className="steps">
        <div className="steps__header" id="features">
          <span className="steps__tag">{t("steps.tag")}</span>
          <h2 className="steps__heading">{t("steps.heading")}</h2>
        </div>

        <Step
          index={1}
          id="step-voice"
          label={t("step1.label")}
          title={t("step1.title")}
          description={t("step1.desc")}
        >
          <VoiceVisual />
        </Step>

        <Step
          index={2}
          id="step-chat"
          label={t("step2.label")}
          title={t("step2.title")}
          description={t("step2.desc")}
          reversed
          pinned={!isMobile}
        >
          <ChatVisual />
        </Step>

        <Step
          index={3}
          id="step-canvas"
          label={t("step3.label")}
          title={t("step3.title")}
          description={t("step3.desc")}
        >
          <CanvasVisual />
        </Step>

        <Step
          index={4}
          id="step-recording"
          label={t("step4.label")}
          title={t("step4.title")}
          description={t("step4.desc")}
          reversed
        >
          <RecordingVisual />
        </Step>
      </div>

      <WorkflowTimeline />
      <CTAFooter />
    </div>
  );
};

export default LandingApp;
